import { Badge } from "@/components/ui/badge";
import {
  HITL_REASON_LABEL,
  HITL_REASON_TO_ENTITY_TYPE,
  type HitlEntityType,
} from "@/lib/tina/constants";
import { cn } from "@/lib/utils";
import type { HitlReason } from "@/types/tina";

const TYPE_STYLES: Record<HitlEntityType, string> = {
  venue: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  artist: "bg-pink-500/15 text-pink-700 dark:text-pink-300",
  event: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  movie: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  play: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  person: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  image: "bg-teal-500/15 text-teal-700 dark:text-teal-300",
  // Indigo — unused by the seven scrape entity types, so an enrichment park is
  // distinguishable at a glance in a mixed queue.
  enrichment: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  // Rose, for the same reason: a refresh review is about an event that is
  // already published, which is the opposite of every other park in the queue.
  refresh: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
};

/**
 * The `hitl_reason` chip, on all four surfaces that show one: the `/tina/items`
 * column, the item show header, the home HITL strip, and `/tina/hitl`.
 *
 * Renders through {@link HITL_REASON_LABEL}, the same map the reason filter
 * dropdown builds its options from. Both sides used to be raw and so they
 * agreed; then the dropdown was labelled and the badge was left printing the
 * wire value, which is how the item show header came to read `Show-genre
 * enrichment` and `enrichment_low_confidence` on one line.
 *
 * The `??` is the same fallback `enrichmentStatusClasses` takes: `reason` is a
 * closed union at compile time but a bare string off the wire, so a reason Tina
 * adds after this deploy shows its raw value rather than `undefined`.
 */
export function HitlReasonBadge({ reason }: { reason: HitlReason }) {
  const entityType = HITL_REASON_TO_ENTITY_TYPE[reason];
  return (
    <Badge className={cn(TYPE_STYLES[entityType])}>
      {HITL_REASON_LABEL[reason] ?? reason}
    </Badge>
  );
}
